import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'dentalstaff_jwt';

const JobseekerAuthContext = createContext(null);

export function JobseekerAuthProvider({ children }) {
  const [jobseeker, setJobseeker] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = useCallback(() => localStorage.getItem(TOKEN_KEY), []);

  const setToken = useCallback((token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  }, []);

  const refreshMe = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setJobseeker(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/jobs/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobseeker(res.data.jobseeker);
    } catch {
      setToken(null);
      setJobseeker(null);
    } finally {
      setLoading(false);
    }
  }, [API, getToken, setToken]);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const signup = async (payload) => {
    const res = await axios.post(`${API}/api/jobs/auth/signup`, payload);
    setToken(res.data.token);
    setJobseeker(res.data.jobseeker);
    return res.data;
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/api/jobs/auth/login`, { email, password });
    setToken(res.data.token);
    setJobseeker(res.data.jobseeker);
    return res.data;
  };

  const logout = () => {
    setToken(null);
    setJobseeker(null);
  };

  const apiRequest = useCallback(async (method, path, data, config = {}) => {
    const token = getToken();
    return axios({
      method,
      url: `${API}${path}`,
      data,
      ...config,
      headers: {
        ...(config.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  }, [API, getToken]);

  return (
    <JobseekerAuthContext.Provider value={{
      jobseeker, loading, signup, login, logout, refreshMe, apiRequest, getToken,
    }}>
      {children}
    </JobseekerAuthContext.Provider>
  );
}

export function useJobseekerAuth() {
  const ctx = useContext(JobseekerAuthContext);
  if (!ctx) throw new Error('useJobseekerAuth must be used inside JobseekerAuthProvider');
  return ctx;
}

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join('; ');
  return err?.message || 'Something went wrong. Please try again.';
}
